type AssistantMessagePart =
  | { type: "text"; text: string }
  | { type: "source-url"; sourceId: string; url: string; title?: string }
  | {
      type: "source-document";
      sourceId: string;
      artifactId: string;
      processingRevisionId: string;
      blockId: string;
      documentName: string;
      pageStart?: number;
      pageEnd?: number;
      headingPath: string[];
      excerpt: string;
    }
  | { type: "plan"; text: string }
  | { type: string };

type AssistantMessage = {
  parts?: AssistantMessagePart[];
  text?: string;
};

export interface AssistantCitationLink {
  sourceId: string;
  path: string;
  title?: string;
}

export interface AssistantDocumentCitationGroup {
  key: string;
  artifactId: string;
  processingRevisionId: string;
  documentName: string;
  pageStart?: number;
  pageEnd?: number;
  headingPath: string[];
  blockIds: string[];
  excerpts: string[];
}

function getAssistantParts(message: AssistantMessage) {
  return Array.isArray(message.parts) ? message.parts : [];
}

function isSourceUrlPart(
  part: AssistantMessagePart
): part is { type: "source-url"; sourceId: string; url: string; title?: string } {
  return (
    part.type === "source-url" &&
    "sourceId" in part &&
    "url" in part &&
    typeof part.sourceId === "string" &&
    typeof part.url === "string"
  );
}

function isSourceDocumentPart(
  part: AssistantMessagePart
): part is Extract<AssistantMessagePart, { type: "source-document" }> {
  return (
    part.type === "source-document" &&
    "artifactId" in part &&
    "processingRevisionId" in part &&
    "blockId" in part &&
    "documentName" in part &&
    "headingPath" in part &&
    "excerpt" in part &&
    typeof part.artifactId === "string" &&
    typeof part.processingRevisionId === "string" &&
    typeof part.blockId === "string" &&
    typeof part.documentName === "string" &&
    Array.isArray(part.headingPath) &&
    typeof part.excerpt === "string"
  );
}

export function getAssistantMessageText(message: AssistantMessage): string {
  const parts = getAssistantParts(message);
  const partText = parts
    .filter((part): part is Extract<AssistantMessagePart, { type: "text" }> => {
      return part.type === "text";
    })
    .map((part) => part.text.trim())
    .filter((text) => text.length > 0)
    .join("\n");

  if (partText.length > 0) {
    return partText;
  }

  return typeof message.text === "string" ? message.text.trim() : "";
}

export interface AssistantPlanPart {
  text: string;
}

function isPlanPart(part: AssistantMessagePart): part is { type: "plan"; text: string } {
  return part.type === "plan" && "text" in part && typeof part.text === "string";
}

export function getAssistantPlanParts(message: AssistantMessage): AssistantPlanPart[] {
  return getAssistantParts(message)
    .filter(isPlanPart)
    .filter((part) => part.text.trim().length > 0);
}

export function getAssistantCitationLinks(message: AssistantMessage): AssistantCitationLink[] {
  const seen = new Set<string>();
  const links: AssistantCitationLink[] = [];

  for (const part of getAssistantParts(message)) {
    if (!isSourceUrlPart(part)) continue;
    if (!part.url.startsWith("/")) continue;

    const key = `${part.sourceId}:${part.url}`;
    if (seen.has(key)) continue;
    seen.add(key);

    links.push({
      sourceId: part.sourceId,
      path: part.url,
      title: part.title,
    });
  }

  return links;
}

/** One processed document an answer stands on, with every passage read from it. */
export interface AssistantDocumentEvidenceDocument {
  key: string;
  artifactId: string;
  processingRevisionId: string;
  documentName: string;
  blockIds: string[];
}

/**
 * Collapses the location-level citation groups into one entry per processed document, in
 * first-seen order. The footer answers "which documents is this built on"; which page inside
 * each of them is what the citation popover is for.
 */
export function groupAssistantDocumentCitationsByDocument(
  groups: readonly AssistantDocumentCitationGroup[]
): AssistantDocumentEvidenceDocument[] {
  const byDocument = new Map<string, AssistantDocumentEvidenceDocument>();

  for (const group of groups) {
    const key = `${group.artifactId}:${group.processingRevisionId}`;
    const existing = byDocument.get(key);
    if (existing) {
      existing.blockIds.push(...group.blockIds);
      continue;
    }
    byDocument.set(key, {
      key,
      artifactId: group.artifactId,
      processingRevisionId: group.processingRevisionId,
      documentName: group.documentName,
      blockIds: [...group.blockIds],
    });
  }

  return [...byDocument.values()];
}

export function getAssistantDocumentCitationGroups(
  message: AssistantMessage
): AssistantDocumentCitationGroup[] {
  const groups: AssistantDocumentCitationGroup[] = [];
  const seenBlocks = new Set<string>();

  for (const part of getAssistantParts(message)) {
    if (!isSourceDocumentPart(part)) continue;
    const blockKey = `${part.artifactId}:${part.processingRevisionId}:${part.blockId}`;
    if (seenBlocks.has(blockKey)) continue;
    seenBlocks.add(blockKey);

    const headingKey = part.headingPath.join("/");
    const groupKey = `${part.artifactId}:${part.processingRevisionId}:${part.pageStart ?? ""}:${part.pageEnd ?? ""}:${headingKey}`;
    const previous = groups.at(-1);
    if (previous?.key === groupKey) {
      previous.blockIds.push(part.blockId);
      previous.excerpts.push(part.excerpt);
      continue;
    }
    groups.push({
      key: groupKey,
      artifactId: part.artifactId,
      processingRevisionId: part.processingRevisionId,
      documentName: part.documentName,
      pageStart: part.pageStart,
      pageEnd: part.pageEnd,
      headingPath: part.headingPath,
      blockIds: [part.blockId],
      excerpts: [part.excerpt],
    });
  }

  return groups;
}
